import type { TimelineEvent } from '@ps/isp-web-component-library';
import type { FootballFixture, FootballIncident } from '@ps/web-shared-generated-graphql/sports';

interface HalfTime {
  currentTimeInMinutes?: number;
  currentExtraTimeInMinutes?: number;
  timelineEvents?: TimelineEvent[];
  extraTimelineEvents?: TimelineEvent[];
}

interface MatchHalves {
  firstHalf: HalfTime;
  secondHalf: HalfTime;
}

interface EventInfo {
  type: TimelineEvent['type'];
  team: 'home' | 'away';
}

interface TimeData {
  firstHalfTime: number;
  secondHalfTime: number;
  firstHalfExtraTime: number;
  secondHalfExtraTime: number;
}

type HalfType = 'firstHalf' | 'secondHalf' | 'firstHalfExtra' | 'secondHalfExtra';

const GOAL_TYPES = ['goal', 'goalCancelled'];
const YELLOW_CARD_TYPES = ['yellow', 'multipleYellow', 'multipleDifferent'];
const RED_CARD_TYPES = ['red', 'multipleRed', 'multipleDifferent'];
const ALLOWED_INCIDENT_GOAL_TYPES = ['CANCELLED', 'NORMAL', 'OWN', 'PENALTY'];
const CARD_TYPE_MAP: Record<string, TimelineEvent['type']> = {
  YELLOW: 'yellow',
  RED: 'red',
  YELLOW_RED: 'yellowRed'
};

const emptyHalfTime = {
  timelineEvents: [],
  extraTimelineEvents: [],
  currentTimeInMinutes: 0,
  currentExtraTimeInMinutes: 0
};

const emptyMatchHalves = {
  firstHalf: emptyHalfTime,
  secondHalf: emptyHalfTime
};

const isGoalType = (type: TimelineEvent['type']): boolean => GOAL_TYPES.includes(type);
const isYellowCardType = (type: TimelineEvent['type']): boolean => YELLOW_CARD_TYPES.includes(type);
const isRedCardType = (type: TimelineEvent['type']): boolean => RED_CARD_TYPES.includes(type);

const calculateRelativeMinute = (
  absoluteMinute: number,
  halfType: HalfType,
  maxMinutes?: number
): number => {
  let minute = absoluteMinute;

  switch (halfType) {
    case 'secondHalf':
      minute -= 45;
      break;
    case 'firstHalfExtra':
      minute -= 90;
      break;
    case 'secondHalfExtra':
      minute -= 105;
      break;
  }

  minute = Math.max(0, minute);

  if (maxMinutes !== undefined) {
    minute = Math.min(minute, maxMinutes);
  }

  return minute;
};

const getEventTypeAndTeam = (incident: FootballIncident): EventInfo | null => {
  if (!('side' in incident.details)) return null;

  const team = incident.details.side === 'HOME' ? 'home' : 'away';

  if (
    'goalType' in incident.details &&
    ALLOWED_INCIDENT_GOAL_TYPES.includes(incident.details.goalType)
  ) {
    const type = incident.details.goalType === 'CANCELLED' ? 'goalCancelled' : 'goal';
    return { type, team };
  }

  if ('cardType' in incident.details && incident.details.cardType in CARD_TYPE_MAP) {
    const type = CARD_TYPE_MAP[incident.details.cardType];
    return { type, team };
  }

  return null;
};

const addOrMergeEvent = (
  incident: FootballIncident,
  halfType: HalfType,
  currentEvents: TimelineEvent[]
): void => {
  if (!incident.clock) return;

  const eventInfo = getEventTypeAndTeam(incident);

  if (!eventInfo) return;

  const { type, team } = eventInfo;
  const incidentMinute = incident.clock.roundMinute || incident.clock.minute || 0;
  const newEvent: TimelineEvent = {
    type,
    minute: calculateRelativeMinute(incidentMinute, halfType),
    team
  };

  const areBothGoals = (type1: TimelineEvent['type'], type2: TimelineEvent['type']) =>
    isGoalType(type1) && isGoalType(type2);

  const areBothCards = (type1: TimelineEvent['type'], type2: TimelineEvent['type']) =>
    ((isYellowCardType(type1) || isRedCardType(type1)) &&
      (isYellowCardType(type2) || isRedCardType(type2))) ||
    [type1, type2].includes('yellowRed');

  const existingEvent = currentEvents.find(
    (event) =>
      event.minute === newEvent.minute &&
      event.team === newEvent.team &&
      (areBothGoals(event.type, newEvent.type) || areBothCards(event.type, newEvent.type))
  );

  if (existingEvent) {
    if (isGoalType(existingEvent.type) && isGoalType(newEvent.type)) {
      if ([existingEvent.type, newEvent.type].includes('goalCancelled')) {
        existingEvent.type = 'goalCancelled';
      }
      return;
    }

    if (existingEvent.team !== newEvent.team || existingEvent.type === 'multipleDifferent') {
      return;
    }

    if (isRedCardType(existingEvent.type) && isRedCardType(newEvent.type)) {
      existingEvent.type = 'multipleRed';
    } else if (isYellowCardType(existingEvent.type) && isYellowCardType(newEvent.type)) {
      existingEvent.type = 'multipleYellow';
    } else {
      existingEvent.type = 'multipleDifferent';
    }
  } else {
    currentEvents.push(newEvent);
  }
};

const calculateCurrentTimes = (
  currentMinute: number,
  currentStatus: string | undefined,
  currentPeriod: string | null | undefined
): TimeData => {
  const times: TimeData = {
    firstHalfTime: 0,
    secondHalfTime: 0,
    firstHalfExtraTime: 0,
    secondHalfExtraTime: 0
  };

  if (!currentStatus) return times;

  if (currentPeriod === 'REGULAR') {
    switch (currentStatus) {
      case 'INPLAY_FIRST_HALF':
        times.firstHalfTime = Math.min(currentMinute, 45);
        break;
      case 'INPLAY_SECOND_HALF':
        times.firstHalfTime = 45;
        times.secondHalfTime = calculateRelativeMinute(currentMinute, 'secondHalf', 45);
        break;
      case 'HALF':
        times.firstHalfTime = 45;
        break;
      case 'FULL':
        times.firstHalfTime = 45;
        times.secondHalfTime = 45;
        break;
      case 'END':
        times.firstHalfTime = 45;
        times.secondHalfTime = 45;
        break;
      case 'PENALTY_SHOOTOUT':
        times.firstHalfTime = 45;
        times.secondHalfTime = 45;
        break;
    }
  } else if (currentPeriod === 'EXTRA') {
    times.firstHalfTime = 45;
    times.secondHalfTime = 45;

    switch (currentStatus) {
      case 'INPLAY_FIRST_HALF':
        times.firstHalfExtraTime = calculateRelativeMinute(currentMinute, 'firstHalfExtra', 15);
        break;
      case 'INPLAY_SECOND_HALF':
        times.firstHalfExtraTime = 15;
        times.secondHalfExtraTime = calculateRelativeMinute(currentMinute, 'secondHalfExtra', 15);
        break;
      case 'HALF':
        times.firstHalfExtraTime = 15;
        break;
      case 'FULL':
        times.firstHalfExtraTime = 15;
        times.secondHalfExtraTime = 15;
        break;
      case 'END':
        times.firstHalfExtraTime = 15;
        times.secondHalfExtraTime = 15;
        break;
      case 'PENALTY_SHOOTOUT':
        times.firstHalfExtraTime = 15;
        times.secondHalfExtraTime = 15;
        break;
    }
  }

  return times;
};

export const mapIncidentsToMatchHalves = (
  data: Partial<FootballFixture> | null | undefined
): MatchHalves => {
  if (!data?.incidents) return emptyMatchHalves;

  const { incidents, duration } = data;
  const currentMinute = duration?.clock?.roundMinute || duration?.clock?.minute || 0;
  const currentStatus = duration?.status;
  const currentPeriod = duration?.period;

  const { firstHalfEvents, secondHalfEvents, firstHalfExtraEvents, secondHalfExtraEvents } =
    incidents.reduce(
      (acc, incident) => {
        if (incident.period === 'REGULAR') {
          if (incident.periodStatus === 'INPLAY_FIRST_HALF') {
            addOrMergeEvent(incident, 'firstHalf', acc.firstHalfEvents);
          } else if (incident.periodStatus === 'INPLAY_SECOND_HALF') {
            addOrMergeEvent(incident, 'secondHalf', acc.secondHalfEvents);
          }
        } else if (incident.period === 'EXTRA') {
          if (incident.periodStatus === 'INPLAY_FIRST_HALF') {
            addOrMergeEvent(incident, 'firstHalfExtra', acc.firstHalfExtraEvents);
          } else if (incident.periodStatus === 'INPLAY_SECOND_HALF') {
            addOrMergeEvent(incident, 'secondHalfExtra', acc.secondHalfExtraEvents);
          }
        }

        return acc;
      },
      {
        firstHalfEvents: [],
        secondHalfEvents: [],
        firstHalfExtraEvents: [],
        secondHalfExtraEvents: []
      }
    );

  const { firstHalfTime, secondHalfTime, firstHalfExtraTime, secondHalfExtraTime } =
    calculateCurrentTimes(currentMinute, currentStatus, currentPeriod);

  return {
    firstHalf: {
      timelineEvents: firstHalfEvents,
      extraTimelineEvents: firstHalfExtraEvents,
      currentTimeInMinutes: firstHalfTime,
      currentExtraTimeInMinutes: firstHalfExtraTime
    },
    secondHalf: {
      timelineEvents: secondHalfEvents,
      extraTimelineEvents: secondHalfExtraEvents,
      currentTimeInMinutes: secondHalfTime,
      currentExtraTimeInMinutes: secondHalfExtraTime
    }
  };
};
